<template>
    <LayoutBlock
    :tutorial_dialog="tutorial_dialog"
    ref="layoutBlock"
    v-on:closeDialog="handleCloseDialog"
    >
    <div class="d-flex flex-column" v-if="measures">
        <div class="pl-2 pb-2 subtitle-1" v-if="conf.title" >{{conf.title}}</div>
        <v-container class="ma-0 pa-0">
            <v-row no-gutters>
                <v-col cols="6">
                    <NumericField dense outlined
                    label="fattore di copertura K"
                    v-model="measures.k"
                    :readonly="preview"
                    />
                </v-col>
                <v-col cols="6">
                    <v-checkbox dense outlined 
                        label="verifica conformità"
                        v-if="!preview" 
                        v-model="measures.useTolerance"></v-checkbox>
                </v-col>
                <v-col cols="12" class="my-2">
                    <span v-if="measures.useTolerance" class="my-2">
                        tolleranza: <i>{{computeToleranceType.type}}, {{computeToleranceType.value}}</i>
                    </span>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12" class="d-flex justify-end py-2" v-if="!preview">
                    <v-btn color="primary" @click="autoFillWrapper(measures.items)">Riempimento automatico</v-btn>
                </v-col>
            </v-row>
            <v-row no-gutters>
            <v-col cols="12">
                <v-simple-table class="measure_table">
                    <template v-slot:default>
                    <thead class="expansion">
                    <tr>
                        <th class="table-text-blocks text-right">{{data.block.conf.headers.count}}</th>
                        <th class="table-text-blocks text-right">{{data.block.conf.headers.nom}} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right">{{data.block.conf.headers.primaryRead}} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right">{{data.block.conf.headers.conv}} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right">{{data.block.conf.headers.reading}} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right">{{data.block.conf.headers.difference}} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right">{{data.block.conf.headers.uncertain}} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right" v-if="measures.useTolerance">{{data.block.conf.headers.toleranceError}}<br/><i class="caption">({{computeToleranceType.uom}})</i></th>
                        <th class="table-text-blocks text-right" v-if="measures.useTolerance">{{data.block.conf.headers.tolerance}}<br/><i class="caption">({{computeToleranceType.uom}})</i></th>
                        <th class="table-text-blocks text-right" v-if="measures.useTolerance">{{data.block.conf.headers.result}}</th>
                    </tr>
                    </thead>
                    <tr v-for="(item,index) of measures.items" :key="index">
                        <td class="table-blocks d-flex justify-center">
                            <v-icon class="d-flex flex-shrink-1 flex-grow-0" v-if="index >= conf.min_readings && !preview" @click="removeRow(index)">delete</v-icon>
                            <div v-else class="d-flex pr-4"></div>
                            <div class="d-flex justify-center flex-shrink-0 flex-grow-1 pr-1">{{index + 1}}</div>
                        </td>
                        <td class="table-blocks">
                            <NumericField dense outlined 
                                @input="debouncedInterpolate(item)" 
                                placeholder="0" 
                                solo hide-details 
                                :readonly="preview" 
                                v-model="item.nom" 
                                :precision="currentPrecision"
                                :class="readingWarning(item.nom)"
                                :negative="true"/>
                        </td>
                        <td class="table-blocks">
                            <NumericField dense outlined 
                                @input="debouncedInterpolate(item)" 
                                placeholder="0" 
                                solo hide-details 
                                :readonly="preview" 
                                v-model="item.primaryRead"
                                :negative="true"/>
                        </td>
                        <td class="table-blocks">
                            <NumericField dense outlined 
                                hide-details 
                                disabled
                                :negative="true"
                                v-model="item.conv"
                                />
                        </td>
                        <td class="table-blocks">
                            <NumericField dense outlined 
                                @input="calculateDiff(item)" 
                                placeholder="0" 
                                :readonly="preview" 
                                :clearable="!preview" 
                                solo hide-details 
                                v-model="item.reading" 
                                :precision="currentPrecision"
                                :class="readingWarning(item.reading)"
                                :negative="true"/>
                        </td>
                        <td class="table-text-blocks  text-right">
                            <b>{{item.difference | formatNumber}}</b>
                        </td>
                        <td class="table-text-blocks  text-right">
                            <b>{{item.uncertain | formatNumber}}</b>                   
                        </td>
                        <td class="table-text-blocks  text-right" v-if="measures.useTolerance">
                            <b>{{item.toleranceError | formatNumber}}</b>
                        </td>
                        <td class="table-text-blocks  text-right" v-if="measures.useTolerance">
                            <b>{{item.tolerance | formatNumber}}</b>
                        </td>
                        <td class="table-text-blocks text-right" v-if="measures.useTolerance">
                            <StatusIcon 
                            :status="item.result" 
                            :tooltip_text="row_tooltip[item.result]" />
                        </td>
                    </tr>
                </template>
                </v-simple-table>
            </v-col>
            <v-col></v-col>
        </v-row>
        <v-row no-gutters>
            <div class="d-flex flex-column flex-grow-1 flex-shrink-0 table-bordered pa-2">
                <div v-if="precision_overflow" class="d-flex error--text caption"><StatusIcon status="failed" small /><b>Controlla valori e precisione letture</b></div>
            </div>        
            <div class="d-flex flex-grow-0 flex-shrink-1 my-2">
                <v-btn v-if="checkStatic && !preview" color="primary" @click="addRow"><v-icon class="pa-2">add_box</v-icon>aggiungi misura</v-btn>
            </div>
       </v-row>
        </v-container>
    </div>
</LayoutBlock>
</template>
<script>
import { thermometer_phmeter_scie } from '../../../utils/itemBlockSchema'
import {BaseIso} from "../../../utils/baseIso"
import LayoutBlock from "../../../components/utils/LayoutBlock"
import StatusIcon from '../../../components/utils/StatusIcon'
// import _ from 'lodash'

export default {
    components: {LayoutBlock, StatusIcon},
    mixins: [BaseIso],
    created() {
        this.debouncedInterpolate = _.debounce(function(item){
            // console.log('debouncedInterpolate')
            this.interpolateValue(item, ["multimetro campione"])
        },1000)
    },
    methods: {
        async buildMeasures(){
            this.measures = {
                items: [],
                tolerance: null,
                k: 2,
                useTolerance: true,
                result: 'uncomplete'
            }
            this.buildTolerances()
            let valuesFromCategory = await this.buildWithCategory()
            if(valuesFromCategory) {
                for(let i = 0; i< this.data.block.conf.min_readings; i++){
                    let item = thermometer_phmeter_scie({
                        nom: valuesFromCategory[i],
                        tolerance: this.measures.tolerance
                    }) 
                    this.measures.items.push(item)
                }
            } else {
                for(let i = 0; i< this.data.block.conf.min_readings; i++){
                    let item = thermometer_phmeter_scie({
                        tolerance: this.measures.tolerance
                    }) 
                this.measures.items.push(item)
                }
            }
        },
        addRow(){
            let item = thermometer_phmeter_scie({
                tolerance: this.measures.tolerance
            })
            this.measures.items.push(item)
        },
    },
}
</script>